import classNames from 'classnames'
import React, { PropsWithChildren, createContext, useEffect, useId, useMemo, useState } from 'react'

import { useArabicSessionLanguage } from '@app/context/SessionSettingsContext'

import { TextColorType } from '@ui/design-system/colors/colors'
import { IconFCType } from '@ui/icons/types'

import { SegmentItem } from './SegmentItem'

import styles from './SegmentedControl.module.scss'

export const SegmentControlContext = createContext<{ layoutId: string; animate: boolean } | null>(
  null
)

export type SegmentedControlSize = 'sm' | 'md'
export type SegmentedControlVariant = 'primary' | 'primary-accent' | 'neutral' | 'default'
export type SegmentedControlMode = 'solid' | 'outline'

export interface SegmentedControlTab {
  id: string | number
  name: React.ReactNode
  onClick?: () => void
  condition?: boolean
  iconLeft?: IconFCType
  iconRight?: IconFCType
  iconColor?: TextColorType
  secondaryIconColor?: TextColorType
}

export interface SegmentedControlProps extends PropsWithChildren {
  variant?: SegmentedControlVariant
  mode?: SegmentedControlMode
  tabs?: SegmentedControlTab[]
  activeTab?: string | number
  initialTab?: string | number
  size?: SegmentedControlSize
  disabled?: boolean
  isMobile?: boolean
  animate?: boolean
}

export const SegmentedControl: React.FC<SegmentedControlProps> = (props) => {
  const {
    tabs,
    activeTab,
    initialTab,
    variant,
    mode = 'solid',
    size = 'md',
    children,
    disabled,
    isMobile,
    animate = true,
  } = props
  const isArabic = useArabicSessionLanguage()
  const uniqueId = useId()
  const layoutId = `${uniqueId}-highlight`

  const isTabsMode = Array.isArray(tabs) && tabs.length > 0

  const availableTabs = useMemo(
    () => (isTabsMode ? tabs.filter((tab) => tab.condition !== false) : []),
    [tabs, isTabsMode]
  )

  const getInitialSelectedTab = () => {
    if (initialTab && availableTabs.some((tab) => tab.id === initialTab)) {
      return initialTab
    }
    return availableTabs.at(0)?.id
  }

  const [selectedTabId, setSelectedTabId] = useState(getInitialSelectedTab)

  useEffect(() => {
    if (!isTabsMode || activeTab !== undefined) return
    const isSelectionValid = availableTabs.some((tab) => tab.id === selectedTabId)
    if (!isSelectionValid) {
      setSelectedTabId(getInitialSelectedTab())
    }
  }, [availableTabs, isTabsMode, activeTab, selectedTabId, getInitialSelectedTab, initialTab])

  const activeIndex = useMemo(() => {
    if (isTabsMode) {
      const currentTabId = activeTab ?? selectedTabId
      return availableTabs.findIndex((t) => t.id === currentTabId)
    }
    return -1
  }, [activeTab, isTabsMode, availableTabs, selectedTabId])

  const handleClick = (idx: number, onClick?: () => void) => {
    setSelectedTabId(availableTabs[idx].id)
    onClick?.()
  }

  const getIconColor = (): TextColorType => {
    switch (variant) {
      case 'primary':
        return 'text-white'
      case 'primary-accent':
        return 'text-primary-high-em'
      case 'neutral':
        return 'text-white'
      default:
        return 'text-high-em'
    }
  }

  const resolvedVariant = variant ?? 'default'
  const iconSize = 16
  const iconColor = getIconColor()

  return (
    <div
      className={classNames(styles.toggleContainer, {
        [styles.rtl]: isArabic,
      })}
      data-variant={resolvedVariant}
      data-mode={mode}
      data-size={size}
      data-mobile={isMobile}
      data-disabled={disabled}
    >
      <SegmentControlContext.Provider value={{ layoutId, animate }}>
        {isTabsMode
          ? availableTabs.map((tab, idx) => (
              <SegmentItem
                key={tab.id}
                tab={tab}
                isActive={idx === activeIndex}
                size={size}
                variant={resolvedVariant}
                disabled={disabled}
                iconSize={iconSize}
                iconColor={tab.iconColor ?? iconColor}
                secondaryIconColor={tab.secondaryIconColor}
                onSelect={() => handleClick(idx, tab.onClick)}
              />
            ))
          : children}
      </SegmentControlContext.Provider>
    </div>
  )
}
